import { CenteredLoader } from '@/components_deprecated/Loader/CenteredLoader'
import SalaryGantt from '@/components/kibo-ui/gantt/salary-gantt'
import {
  enforceHourMinuteString,
  generateActivityMapColors,
} from '@/modules/activities'
import { Map } from '@/modules/activities/components/detail'
import type { VehicleColorDictionary } from '@/modules/activities/types'
import { WORK_PERIOD } from '@/modules/salary/queries'
import { useQuery } from '@apollo/client'
import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import styled from 'styled-components'

type WorkPeriodHoursAndMapView = 'all' | 'time' | 'map'

const Layout = styled.div<{
  $showMap: boolean
  $view: WorkPeriodHoursAndMapView
}>`
  display: grid;
  grid-template-columns: ${props => {
    if (props.$view !== 'all') return 'minmax(0, 1fr)'
    return props.$showMap
      ? 'minmax(0, 1fr) 2.5rem minmax(320px, 0.55fr)'
      : 'minmax(0, 1fr) 2.5rem'
  }};
  gap: 1rem;
  align-items: start;
  padding: 1rem 0;

  ${props => props.theme.media.mobile} {
    grid-template-columns: 100%;
  }
`

const GanttPanel = styled.div`
  min-width: 0;
  overflow-x: auto;
`

const SummaryTable = styled.table`
  width: 100%;
  margin-top: 1rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
  background: ${props => props.theme.colors.white};
  table-layout: fixed;

  th,
  td {
    padding: 0.55rem 0.75rem;
    border-bottom: 1px solid ${props => props.theme.colors.gray8};
    text-align: left;
    vertical-align: top;
  }

  th {
    background: ${props => props.theme.colors.gray11};
    color: ${props => props.theme.colors.gray2};
    font-weight: 600;
  }

  td:last-child,
  th:last-child {
    text-align: right;
    width: 11rem;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`

const SummaryValue = styled.span`
  font-weight: 600;
`

const GanttMeta = styled.div`
  margin-top: 0.25rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  line-height: 1.35;
`

const VehicleList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
`

const VehicleBadge = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.15rem 0.45rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.gray2};
  font-weight: 600;

  &::before {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 999px;
    background: ${props => props.$color ?? props.theme.colors.gray6};
  }
`

const MapPanel = styled.div`
  min-width: 0;
`

const StyledMap = styled(Map)`
  height: 420px;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
`

const StateBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 420px;
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.gray3};
  text-align: center;
`

const ToggleColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 420px;

  ${props => props.theme.media.mobile} {
    min-height: auto;
    justify-content: flex-end;
    margin: -0.25rem 0;
  }
`

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 3rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.gray2};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.colors.gray11};
  }
`

export const WorkPeriodHoursAndMap: React.FC<{
  workPeriodId: string
  activitiesOnly?: boolean
  showMap: boolean
  onToggleMap: () => void
  view?: WorkPeriodHoursAndMapView
}> = ({ workPeriodId, activitiesOnly, showMap, onToggleMap, view = 'all' }) => {
  const translations = useTranslate({
    noData: 'common.no-data',
    map: 'common.map',
    vehicles: 'common.vehicles',
    unknownVehicle: 'common.unknown-vehicle',
    drivingGrade: 'activities.driving-grade',
    activity: 'common.activity',
    value: 'common.value',
    timeDriving: 'common.driving',
    timePause: 'common.pause',
    timeAvailability: 'common.availability',
    timeOtherWork: 'common.other-work',
    timeWorkTotal: 'common.total-work',
    paid: 'common.paid',
    unpaid: 'common.unpaid',
  })

  const { data, loading } = useQuery(WORK_PERIOD, {
    variables: { id: workPeriodId },
  })

  const vehicleLocations = useMemo(
    () => data?.workPeriod?.drivenCoordinates ?? [],
    [data?.workPeriod?.drivenCoordinates]
  )
  const drivingGrade =
    data?.workPeriod?.drivingGrade ??
    data?.workPeriod?.driverGrade?.grade ??
    null
  const hasCoordinates = vehicleLocations.some(
    location => location.coordinates.length > 0
  )

  const vehicleColors = useMemo<VehicleColorDictionary>(() => {
    const vehicleObject: VehicleColorDictionary = {}
    const vehicleVinList = vehicleLocations
      .map(drivenCoordinate => drivenCoordinate.vehicleVin ?? 'Unknown')
      .filter((value, index, vehicles) => vehicles.indexOf(value) === index)
    const vehicleColorList = generateActivityMapColors(vehicleVinList.length)

    vehicleVinList.forEach((vehicle, idx) => {
      vehicleObject[vehicle] = vehicleColorList[idx] ?? '#0088FF'
    })

    return vehicleObject
  }, [vehicleLocations])

  const usedVehicles = useMemo(() => {
    const vehicles: { label: string; vehicleVin: string | null }[] = []
    const seenVehicles = new Set<string>()

    const addVehicle = (label?: string | null, vehicleVin?: string | null) => {
      const normalizedLabel =
        label?.trim() || vehicleVin?.trim() || translations.unknownVehicle

      if (seenVehicles.has(normalizedLabel)) return

      vehicles.push({
        label: normalizedLabel,
        vehicleVin: vehicleVin?.trim() || null,
      })
      seenVehicles.add(normalizedLabel)
    }

    data?.workPeriod?.activities?.forEach(activity => {
      addVehicle(activity.vehicleRegistrationNumber, activity.vehicleVin)
    })

    if (vehicles.length === 0) {
      vehicleLocations.forEach(location => {
        addVehicle(null, location.vehicleVin)
      })
    }

    return vehicles
  }, [
    data?.workPeriod?.activities,
    translations.unknownVehicle,
    vehicleLocations,
  ])

  const activityTimes = data?.workPeriod?.activityTimes
  const showGanttPanel = view !== 'map'
  const showMapPanel = view === 'map' || (view === 'all' && showMap)

  return (
    <Layout $showMap={showMap} $view={view}>
      {showGanttPanel && (
        <GanttPanel>
          <SalaryGantt
            workPeriodId={workPeriodId}
            activitiesOnly={activitiesOnly}
            fullWidth={view === 'time' || !showMap}
          />
          {drivingGrade && (
            <GanttMeta>
              {translations.drivingGrade}: {drivingGrade}
            </GanttMeta>
          )}
          <SummaryTable>
            <thead>
              <tr>
                <th>{translations.activity}</th>
                <th>{translations.value}</th>
              </tr>
            </thead>
            <tbody>
              {loading && !activityTimes ? (
                <tr>
                  <td colSpan={2}>
                    <CenteredLoader marginTop="0" size={24} thickness={3} />
                  </td>
                </tr>
              ) : activityTimes ? (
                <>
                  <tr>
                    <td>{translations.timeWorkTotal}</td>
                    <td>
                      <SummaryValue>
                        {enforceHourMinuteString(activityTimes.timeWorkTotal)}
                      </SummaryValue>
                    </td>
                  </tr>
                  <tr>
                    <td>{translations.timeDriving}</td>
                    <td>
                      <SummaryValue>
                        {enforceHourMinuteString(activityTimes.timeDriving)}
                      </SummaryValue>
                    </td>
                  </tr>
                  <tr>
                    <td>{translations.timeOtherWork}</td>
                    <td>
                      <SummaryValue>
                        {enforceHourMinuteString(activityTimes.timeOtherWork)}
                      </SummaryValue>
                    </td>
                  </tr>
                  <tr>
                    <td>{translations.timeAvailability}</td>
                    <td>
                      <SummaryValue>
                        {enforceHourMinuteString(
                          activityTimes.timeAvailability
                        )}
                      </SummaryValue>
                    </td>
                  </tr>
                  <tr>
                    <td>{translations.timePause}</td>
                    <td>
                      {translations.paid}:{' '}
                      <SummaryValue>
                        {enforceHourMinuteString(activityTimes.timePaidRest)}
                      </SummaryValue>
                      <br />
                      {translations.unpaid}:{' '}
                      <SummaryValue>
                        {enforceHourMinuteString(activityTimes.timePause)}
                      </SummaryValue>
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={2}>{translations.noData}</td>
                </tr>
              )}
            </tbody>
          </SummaryTable>
        </GanttPanel>
      )}

      {view === 'all' && (
        <ToggleColumn>
          <ToggleButton
            type="button"
            onClick={onToggleMap}
            title={translations.map}
            aria-label={translations.map}
            aria-expanded={showMap}
          >
            <Icon icon={showMap ? 'chevron-right' : 'chevron-left'} />
          </ToggleButton>
        </ToggleColumn>
      )}

      {showMapPanel && (
        <MapPanel>
          {loading ? (
            <StateBox>
              <CenteredLoader marginTop="0" size={28} thickness={3} />
            </StateBox>
          ) : hasCoordinates ? (
            <StyledMap
              vehicleLocations={vehicleLocations}
              pathColors={vehicleColors}
              mapContainerStyle={{ borderTopRightRadius: 0 }}
            />
          ) : (
            <StateBox>{translations.noData}</StateBox>
          )}
          {!loading && (
            <VehicleList>
              <span>{translations.vehicles}:</span>
              {usedVehicles.length > 0
                ? usedVehicles.map(vehicle => (
                    <VehicleBadge
                      key={vehicle.label}
                      $color={
                        vehicle.vehicleVin
                          ? vehicleColors[vehicle.vehicleVin]
                          : undefined
                      }
                    >
                      {vehicle.label}
                    </VehicleBadge>
                  ))
                : translations.noData}
            </VehicleList>
          )}
        </MapPanel>
      )}
    </Layout>
  )
}
